export const LOSI_SPLIT_TARGET_MIME = "application/x-losi-split-target"

export type SplitPaneId = "left" | "right"

export type SplitPaneSpaceItemType =
  | "conversation"
  | "folder"
  | "note"
  | "sheet"
  | "sprint"
  | "task"

export type SplitPaneTarget =
  | {
      kind: "chat"
      chatId?: string | null
      draftId?: string | null
      highlightMessageId?: string | null
    }
  | {
      campaignId?: string | null
      contactId?: string | null
      kind: "crm"
      leadId?: string | null
      recordId?: string | null
      tab?: string | null
      workspaceId?: string | null
    }
  | {
      kind: "marketing-emails"
    }
  | {
      kind: "spaces"
      section: "list" | "pulse"
    }
  | {
      kind: "space"
      item?: {
        id: string
        type: SplitPaneSpaceItemType
      } | null
      spaceId: string
    }
  | {
      kind: "space-design"
      designId: string
      spaceId: string
    }
  | {
      kind: "space-whiteboard"
      spaceId: string
      whiteboardId: string
    }
  | {
      content?: string | null
      id: string
      kind: "virtual-file"
      name: string
      path: string
    }
  | {
      href: string
      kind: "unsupported"
      title: string
    }

const SUPPORTED_SPACE_ITEM_TYPES = new Set<SplitPaneSpaceItemType>([
  "conversation",
  "folder",
  "note",
  "sheet",
  "sprint",
  "task"
])

const PANE_ROUTE_PREFIXES = [
  "/admin",
  "/api-credits",
  "/api-usage",
  "/apps",
  "/applications",
  "/assistants",
  "/chat",
  "/crm",
  "/dashboard",
  "/engines",
  "/feature-flags",
  "/help",
  "/integrations",
  "/items",
  "/nexus",
  "/notes",
  "/nudges",
  "/quick-ai-readiness",
  "/settings",
  "/sites",
  "/spaces",
  "/splitview",
  "/studio",
  "/subscription",
  "/templates"
]

const getRouteHref = (url: URL) => `${url.pathname}${url.search}${url.hash}`

let draftChatCounter = 0

const createDraftChatId = () => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID()
  }

  draftChatCounter += 1
  return `draft-${Date.now()}-${draftChatCounter}`
}

const decodeSegment = (value: string | undefined) => {
  if (!value) return null

  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

export function parseSplitPaneHref(
  href: string | null | undefined,
  origin = "http://localhost"
): SplitPaneTarget | null {
  if (!href || href.startsWith("#")) {
    return null
  }

  if (/^(mailto|tel|javascript):/i.test(href)) {
    return null
  }

  let url: URL

  try {
    url = new URL(href, origin)
  } catch {
    return null
  }

  if (url.origin !== origin) {
    return null
  }

  const pathname = url.pathname.replace(/\/+$/, "") || "/"
  const hrefForTarget = getRouteHref(url)

  if (pathname === "/chat") {
    return createSplitPaneDraftChatTarget({
      highlightMessageId: url.searchParams.get("highlight")
    })
  }

  if (
    pathname === "/crm" ||
    pathname.startsWith("/crm/") ||
    pathname === "/nexus/crm" ||
    pathname.startsWith("/nexus/crm/")
  ) {
    const crmPath = getCrmPathState(pathname)

    return {
      campaignId: url.searchParams.get("campaignId") ?? crmPath.campaignId,
      contactId: url.searchParams.get("contactId") ?? crmPath.contactId,
      kind: "crm",
      leadId: url.searchParams.get("leadId") ?? crmPath.leadId,
      recordId: url.searchParams.get("recordId") ?? url.searchParams.get("entityId"),
      tab: getCrmTabFromUrl(pathname, url.searchParams) ?? crmPath.tab,
      workspaceId: url.searchParams.get("workspaceId") ?? url.searchParams.get("workspace_id")
    }
  }

  if (pathname === "/marketing-emails" || pathname === "/admin/marketing-emails") {
    return {
      kind: "marketing-emails"
    }
  }

  const chatMatch = pathname.match(/^\/chat\/([^/]+)$/)
  if (chatMatch) {
    return {
      chatId: decodeSegment(chatMatch[1]),
      highlightMessageId: url.searchParams.get("highlight"),
      kind: "chat"
    }
  }

  if (pathname === "/spaces") {
    return {
      kind: "spaces",
      section: "list"
    }
  }

  if (pathname === "/spaces/planner" || pathname === "/spaces/pulse") {
    return {
      kind: "spaces",
      section: "pulse"
    }
  }

  const whiteboardMatch = pathname.match(
    /^\/spaces\/([^/]+)\/whiteboards\/([^/]+)$/
  )
  if (whiteboardMatch) {
    return {
      kind: "space-whiteboard",
      spaceId: decodeSegment(whiteboardMatch[1]) ?? "",
      whiteboardId: decodeSegment(whiteboardMatch[2]) ?? ""
    }
  }

  const designMatch = pathname.match(/^\/spaces\/([^/]+)\/designs\/([^/]+)$/)
  if (designMatch) {
    return {
      designId: decodeSegment(designMatch[2]) ?? "",
      kind: "space-design",
      spaceId: decodeSegment(designMatch[1]) ?? ""
    }
  }

  const spaceMatch = pathname.match(/^\/spaces\/([^/]+)$/)
  if (spaceMatch) {
    const itemType = url.searchParams.get("itemType")
    const itemId = url.searchParams.get("itemId")
    const spaceId = decodeSegment(spaceMatch[1]) ?? ""

    if (
      itemType &&
      itemId &&
      SUPPORTED_SPACE_ITEM_TYPES.has(itemType as SplitPaneSpaceItemType)
    ) {
      return {
        item: {
          id: itemId,
          type: itemType as SplitPaneSpaceItemType
        },
        kind: "space",
        spaceId
      }
    }

    return {
      item: null,
      kind: "space",
      spaceId
    }
  }

  if (
    PANE_ROUTE_PREFIXES.some(
      prefix => pathname === prefix || pathname.startsWith(`${prefix}/`)
    )
  ) {
    return {
      href: hrefForTarget,
      kind: "unsupported",
      title: getUnsupportedTargetTitle(pathname)
    }
  }

  return null
}

export function createSplitPaneDraftChatTarget(
  options: { highlightMessageId?: string | null } = {}
): Extract<SplitPaneTarget, { kind: "chat" }> {
  return {
    draftId: createDraftChatId(),
    highlightMessageId: options.highlightMessageId ?? null,
    kind: "chat"
  }
}

export function freshenSplitPaneTarget(target: SplitPaneTarget): SplitPaneTarget {
  if (target.kind === "chat" && !target.chatId && !target.draftId) {
    return createSplitPaneDraftChatTarget({
      highlightMessageId: target.highlightMessageId
    })
  }

  return target
}

export function splitPaneTargetToHref(target: SplitPaneTarget): string {
  switch (target.kind) {
    case "chat": {
      const baseHref = target.chatId
        ? `/chat/${encodeURIComponent(target.chatId)}`
        : "/chat"
      return target.highlightMessageId
        ? `${baseHref}?highlight=${encodeURIComponent(target.highlightMessageId)}`
        : baseHref
    }
    case "crm": {
      const params = new URLSearchParams()
      if (target.tab) params.set("tab", target.tab)
      if (target.workspaceId) params.set("workspaceId", target.workspaceId)
      if (target.leadId) params.set("leadId", target.leadId)
      if (target.contactId) params.set("contactId", target.contactId)
      if (target.campaignId) params.set("campaignId", target.campaignId)
      if (target.recordId) params.set("recordId", target.recordId)
      const query = params.toString()
      return query ? `/crm?${query}` : "/crm"
    }
    case "marketing-emails":
      return "/marketing-emails"
    case "spaces":
      return target.section === "pulse" ? "/spaces/planner" : "/spaces"
    case "space": {
      const baseHref = `/spaces/${encodeURIComponent(target.spaceId)}`
      return target.item
        ? `${baseHref}?itemType=${encodeURIComponent(target.item.type)}&itemId=${encodeURIComponent(target.item.id)}`
        : baseHref
    }
    case "space-design":
      return `/spaces/${encodeURIComponent(target.spaceId)}/designs/${encodeURIComponent(target.designId)}`
    case "space-whiteboard":
      return `/spaces/${encodeURIComponent(target.spaceId)}/whiteboards/${encodeURIComponent(target.whiteboardId)}`
    case "virtual-file":
      return `/virtual-files/${encodeURIComponent(target.id)}`
    case "unsupported":
      return target.href
  }
}

export function getSplitPaneTargetKey(target: SplitPaneTarget): string {
  if (target.kind === "virtual-file") {
    return `virtual-file:${target.id}:${target.path}`
  }

  if (target.kind === "chat" && !target.chatId && target.draftId) {
    return `${splitPaneTargetToHref(target)}::${target.draftId}`
  }

  return splitPaneTargetToHref(target)
}

export function getSplitPaneTargetTitle(target: SplitPaneTarget): string {
  switch (target.kind) {
    case "chat":
      return target.chatId ? "Chat" : "New Chat"
    case "crm":
      return target.tab ? `CRM ${capitalize(target.tab)}` : "CRM"
    case "marketing-emails":
      return "Marketing emails"
    case "spaces":
      return target.section === "pulse" ? "Planner" : "Spaces"
    case "space":
      if (!target.item) return "Space"
      return `${capitalize(target.item.type)}`
    case "space-design":
      return "Design"
    case "space-whiteboard":
      return "Whiteboard"
    case "virtual-file":
      return target.name || "Virtual file"
    case "unsupported":
      return target.title
  }
}

export function setSplitPaneDragData(dataTransfer: DataTransfer, href: string) {
  dataTransfer.setData(LOSI_SPLIT_TARGET_MIME, href)
}

export function getSplitPaneDragHref(dataTransfer: DataTransfer): string | null {
  const types = Array.from(dataTransfer.types ?? [])
  if (!types.includes(LOSI_SPLIT_TARGET_MIME)) {
    return null
  }

  return dataTransfer.getData(LOSI_SPLIT_TARGET_MIME) || null
}

function capitalize(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1)
}

function getCrmTabFromUrl(pathname: string, searchParams: URLSearchParams) {
  const tab = searchParams.get("tab")
  if (tab) return tab

  const [, section] = pathname.match(/^\/(?:nexus\/)?crm\/([^/]+)/) ?? []
  if (!section) return null

  return section === "leads" ? "contacts" : section
}

function getCrmPathState(pathname: string) {
  const [, section, id] = pathname.match(/^\/(?:nexus\/)?crm\/([^/]+)\/?([^/]*)/) ?? []
  const decodedId = decodeSegment(id)
  const tab = section === "leads" ? "contacts" : (section ?? null)

  return {
    campaignId: section === "campaigns" ? decodedId : null,
    contactId: section === "contacts" ? decodedId : null,
    leadId: section === "leads" ? decodedId : null,
    tab
  }
}

function getUnsupportedTargetTitle(pathname: string) {
  const [firstSegment] = pathname.split("/").filter(Boolean)

  if (!firstSegment) {
    return "Page"
  }

  return firstSegment
    .split("-")
    .map(segment => capitalize(segment))
    .join(" ")
}
